// Adapter: MeshviewPacket (api/types.ts) -> the existing Packet shape, so a
// meshview firehose packet can flow through the already-built, already-
// verified animation pipeline (lib/animation.ts's makeAnimatedPacket/
// capAnimations/pruneExpired, LiveMap.tsx's renderFrame/colorForAnimCategory)
// completely unchanged. Deliberately the ONLY place a MeshviewPacket is
// coerced into Packet's shape — every field not meaningful for a meshview
// row (rssi, hop_start/hop_limit, relay_node, route_hops, payload) is left
// null/undefined rather than guessed, same "honest gap, not a fabricated
// value" convention the rest of this app already follows.
import type { MeshviewPacket, MeshviewRouteTreeEdge, MeshviewRouteTreeResponse, Packet } from "../api/types";
import { MESHVIEW_MAX_PLAUSIBLE_HOP_KM, MESHVIEW_NULL_ISLAND_EPSILON_DEG, type MeshviewRouteConfidence } from './definitions/map';
import { meshtasticPortnumName, type MeshviewQueuePriority } from './definitions/protocols';
import {
  FLIGHT_MS,
  MESHVIEW_RECEPTION_FADE_MS,
  MESHVIEW_RECEPTION_HOLD_MS,
  MESHVIEW_ROUTE_SIBLING_SPREAD_MS,
  MESHVIEW_TRAIL_MS,
  MESHVIEW_TRAIL_SLICES,
} from "./animation";

// Prefixed so a meshview packet's synthetic id/hash can never collide with a
// real first-party packet's id/hash in the shared `animations` array (both
// arrive keyed by `packet.hash || packet.id` in a couple of call sites).
// meshviewPacketId is an opaque string (see api/types.ts's MeshviewPacket
// comment — some ids exceed Number.MAX_SAFE_INTEGER) — never Number()'d.
export function meshviewAnimationId(meshviewPacketId: string): string {
  return `meshview-${meshviewPacketId}`;
}

export function meshviewPacketToPacket(mp: MeshviewPacket): Packet {
  const id = meshviewAnimationId(mp.meshview_packet_id);
  return {
    id,
    ts: mp.imported_at ?? "",
    protocol: mp.protocol,
    observer_id: mp.observer_id,
    from_id: mp.from_node_id,
    to_id: mp.to_node_id,
    // meshview's contract reports a raw Meshtastic portnum, not the string
    // PortNum name /api/packets sends — see meshtasticPortnumName's own
    // comment (definitions.ts) for why this is the one place that gap is
    // bridged, and why anything unmapped honestly falls to "other" rather
    // than a guess.
    type: mp.protocol === "meshtastic" ? meshtasticPortnumName(mp.portnum) : "other",
    channel: mp.channel,
    snr: mp.reception_summary?.best_snr ?? null,
    rssi: null,
    hop_start: null,
    hop_limit: mp.reception_summary?.max_hops ?? null,
    relay_node: null,
    summary: mp.from_name || mp.from_node_id || "meshview packet",
    hash: id,
    trust_tier: mp.trust_tier,
    path: null,
    route_hops: null,
    payload: null,
  };
}

// --- meshview route-tree walk --------
// The concept error in the original flat fan was direct: there
// should never be a packet that hops clear across the entire map
// — it needs to flow it through the heard feeders as if that's the route
// it was taking. GET /api/meshview/packets/{id}/route-tree (a SIBLING of
// /receptions, not a replacement — see api/types.ts's own header comment)
// gives exactly that: a tree rooted at the transmitter where every edge is
// one short adjacent-node hop, inferred from confirmed direct-link data.
//
// The tree was first turned into one FULL PATH per reached node (root
// through every ancestor to that node). That was wrong in a different way,
// caught watching the live map: it still read as machine-gunning from a
// single location. A packet whose tree reaches 39 nodes spawned 39 separate
// travellers, EVERY ONE departing from the root — a real packet is relayed
// once per node, not re-emitted once per destination. Verified this was a
// rendering-model bug, not a data one, before touching anything: top
// transmitter is 5 of the last 200 packets (no node dominates), longest
// consecutive same-origin run in arrival order is 1, zero duplicate packet
// ids, 30 live route-trees gave 26 distinct roots at 25 distinct positions.
//
// buildRouteWalk below fixes this as a BRANCHING WAVE: one traveller per
// EDGE, not per reached node. A node is departed-from once per arrival, not
// once per downstream leaf — when a traveller arrives at a node, that
// arrival is what spawns the onward travellers along that node's own child
// edges. The trunk out of the root is therefore drawn once (by the single
// traveller crossing it), then splits into as many travellers as the next
// node has children, and so on — a wavefront branching outward, not a spray
// of parallel full-length paths from one point. Each edge's own departure
// offset still comes from a real traversal (a node at level N only becomes
// reachable once its parent has arrived — an edge at level N only
// begins once its parent edge has arrived; the wavefront spreads through
// the mesh) — that cascading-arrival bookkeeping is unchanged; only the
// unit of travel (one edge, not one root-to-leaf chain) changed back.
//
// A literal ring (an earlier attempt, reverted) was wrong for a different
// reason than the spray it replaced —
// a geographic circle reads as too literal and isn't correct: it asserts
// the packet reached everything inside that radius, which terrain
// and antenna patterns make false. The actual root cause of the spray
// wasn't the branching-wave shape at all: it was drawing a full travelling
// LINE for every edge regardless of how little we actually know about it.
// Measured across 40 live trees, 821 edges by confidence tier:
//   proven      47  ( 5%)     evidenced  133 (16%)
//   inferred   370 (45%)      assumed    271 (33%)
//   max children from one node: ALL edges -> median 8, worst 21
//                          JUSTIFIED (proven+evidenced) only -> median 2, worst 11
// "evidenced"/"proven" are the only tiers backed by a direct reception or a
// confirmed direct link — real, physically short hops (median under 10km,
// max 39km measured). "inferred"/"assumed" are the backend's own best
// GUESS at ancestry (79% of edges); asserting a drawn line for a guess is
// exactly what produced both the spray and, separately, 444km "single
// hops" for the "assumed" fallback specifically (uncapped by the backend's
// own plausibility limit, unlike "inferred").
//
// A subsequent JUSTIFIED-only gate (drawing a line only for proven/
// evidenced edges) was reverted: it left the ~79% "inferred"/"assumed"
// majority of every tree as a flash with no
// line, so the only visible lines were first-party rays into the local
// radio. The actual requirement on the weak tier was that the "assumed"
// tier should read as the faintest — drawn, but faint — never
// undrawn; that distinction was lost building the gate above.
//
// So: every edge with a usable parent, a usable child, and a plausible
// distance (MESHVIEW_MAX_PLAUSIBLE_HOP_KM — unchanged: there should
// never be a packet that hops clear across the entire map)
// becomes a drawn MeshviewRoutePath, comet trail and all, REGARDLESS of
// confidence tier. Confidence no longer decides whether a line is drawn —
// only how it's styled (meshviewRouteConfidenceStyle, definitions.ts;
// proven strongest, assumed faintest but clearly present, already tiered
// for all four confidences before this revert, just unused for lines until
// now). Only two cases still fall back to a flash-only receiver: a gap/
// null-parent edge (the backend refused to place an intermediate
// hop, so no line is ever asserted across it) and an edge whose own
// endpoints exceed the plausible-hop backstop (a bad-position
// case) — in both, the receiver still honestly lights up, just with no
// asserted link.
//
// The line-vs-flash choice above turned out to be a
// false either/or. Median real hop distance is under 10km, which at the
// default region-wide zoom is roughly 6 screen pixels — too small to carry
// visibility on its own, measured live both ways: with the (now-reverted)
// justified-only gate flashes peaked at 133px and were visible,
// but lines were rare and effectively missing; with the gate removed
// (every edge a line, no automatic flash) lines drew but flashes dropped to
// 0 and the map went nearly empty (peak flash 7px, peak line 6px, same
// live measurement). Neither is "the" fix — the flash and the line are
// answering different questions ("was this node heard" vs. "what path did
// it take") and both are true at once. So every edge whose child has a
// usable position now flashes UNCONDITIONALLY (this is what a gap/
// over-distance edge already did), and separately, independently, an edge
// that is also plausible-distance with a usable parent additionally gets a
// line. A plausible edge's flash is timed to startOffsetMs+totalTravelMs —
// the same instant its line's travelling head finishes crossing it — so
// the line landing and the node lighting up read as one arrival event, not
// two staggered ones.

export interface MeshviewRoutePathSegment {
  parent: { lon: number; lat: number };
  child: { lon: number; lat: number };
  confidence: MeshviewRouteConfidence | string;
  // "traceroute" | "inferred" (backend Task 28) — carried per-segment same
  // as confidence, so LiveMap.tsx can style a real recorded traceroute hop
  // distinctly from an inferred one even mid-trail.
  provenance: string;
}

export interface MeshviewRoutePath {
  // Exactly one segment: this ONE edge's own hop (root->child for a direct
  // child, parent->child for any deeper edge) — never an accumulated
  // ancestor chain (see this section's header comment for why: that was
  // the "39 travellers from one root" bug). Kept as a 1-element array
  // rather than a bare segment so positionAlongPath/trailSlices below
  // (shared with the pre-fix full-path shape) need no changes.
  segments: MeshviewRoutePathSegment[];
  // This edge's own departure delay from the walk's `createdAt` —
  // precomputed once, at fetch time (a real tree traversal, not a formula
  // LiveMap.tsx would otherwise have to re-derive every frame): the parent
  // edge's own arrival time, plus this edge's own sibling stagger.
  startOffsetMs: number;
  // Always FLIGHT_MS now (one hop, constant speed) — kept as its own field
  // rather than inlining FLIGHT_MS at every call site, and because
  // routeWalkPathState's signature already takes it generically.
  totalTravelMs: number;
}

// A reception at one node (Task 35 — every edge whose child has a usable
// position gets one of these, unconditionally, whether or not that same
// edge ALSO got a MeshviewRoutePath above): no departure/travel of its own
// (it doesn't move — it appears already-heard) — just its real position
// lighting up once the propagation reaches it. `startOffsetMs` doubles as
// routeWalkPathState's own `startOffsetMs` with totalTravelMs=0 (see
// LiveMap.tsx's render effect), so the SAME shared appear/hold/fade state
// machine the ray and comet-trail paths already use governs it too — it
// simply starts already "arrived" the instant its own time comes.
export interface MeshviewReceiverFlash {
  lon: number;
  lat: number;
  confidence: MeshviewRouteConfidence | string;
  startOffsetMs: number;
}

// One packet's held route-tree walk — AppData.tsx owns this data (fetched
// once per newly-seen meshview packet, capped at an ADAPTIVE concurrency
// limit — see lib/animation.ts's MESHVIEW_CAP_* and AppData.tsx's own
// controller), LiveMap.tsx turns it into a GL line layer and computes each
// path's own travel/trail/fade state from `createdAt` + its own
// precomputed departure offset on its own render clock — the same
// AppData-holds-data/LiveMap-computes-frame split already used for the
// ray/pulse `AnimatedPacket` above.
export interface MeshviewRouteWalk {
  key: string;
  packetId: string;
  // Every edge with a usable parent/child and a plausible distance becomes
  // a drawn line, regardless of confidence tier (Task 34 — confidence
  // gates styling, not whether a line is drawn; see this section's header
  // comment). Task 35: this is additive with `flashes` below, not
  // exclusive of it — a plausible edge appears in BOTH arrays.
  paths: MeshviewRoutePath[];
  // Every edge whose child has a usable position (Task 35 — no longer only
  // the gap/null-parent and over-distance cases; those are just the ones
  // that ALSO don't make it into `paths`). This is what makes reception
  // visible at region zoom, where a real hop's line is only a few pixels.
  flashes: MeshviewReceiverFlash[];
  createdAt: number; // performance.now() at fetch-completion time
  // Drain/eviction priority for this packet (lib/definitions.ts's
  // meshviewQueuePriorityForPortnum) — carried through so AppData.tsx's
  // concurrency-cap eviction can protect high-priority packets (traceroute,
  // text message) from being the one dropped when the cap bites. This is
  // ONLY an eviction-order protection (never dropped first), never a
  // release-order boost — see AppData.tsx's own scheduler, which already
  // releases every packet strictly at its own timestamp regardless of this
  // field.
  priority: MeshviewQueuePriority;
  // The packet's protocol and type name, for the packet-type colour of its lines (set by AppData when the walk is held).
  category?: { protocol: string; type: string | null };
  // The single latest finish time (startOffsetMs + totalTravelMs) across
  // every path AND flash in this walk — used only by pruneExpiredRouteWalks
  // below to compute this walk's own true expiry (tree depth/shape varies
  // packet to packet; never a guessed fixed constant).
  maxFinishMs: number;
}

// A `!= null` check ALONE does not actually exclude (0, 0) — 0 is a real
// number, not null/undefined, so a bare null check lets it straight
// through. Verified live before assuming otherwise: 2 of 30 sampled
// route-tree roots sit at exactly (0, 0) (the ingest-side bad-position
// sentinel — see the DB's own float-default convention), and without this
// explicit exclusion buildRouteWalk built and RETURNED a walk anchored at
// null island for one of them (confirmed with a synthetic (0,0)-root tree)
// — not the "already dropped cleanly" behaviour a first read of the `!=
// null` check suggests.
//
// Exact equality alone still isn't enough — a node whose GPS
// fix failed sometimes lands a fraction of a degree from (0, 0) instead of
// exactly on it (0.058/0.032 and 0.013/0.013 found live, root-caused from a
// traceroute segment measuring 11,818km — almost exactly null
// island; see MESHVIEW_MAX_PLAUSIBLE_HOP_KM's own comment in definitions.ts
// for the full trace), and `0.013 === 0` is false, so the old strict check
// let it straight through the same way a bare `!= null` check let literal
// (0, 0) through before. MESHVIEW_NULL_ISLAND_EPSILON_DEG (definitions.ts)
// is deliberately a small, literal degrees-from-origin epsilon, never a
// broader magnitude/regional check — seeded from a live case that turned
// out to include real nodes far outside this deployment's core coverage
// area, which must never be excluded by this rule.
//
// This is the one place both gaps are closed, so every caller (root check
// below, both edge-position checks, and AppData.tsx's own pulse-origin
// checks, which import this exact function) gets the SAME
// definition of "usable" for free, never a second, looser one: if there is
// no origin to put it at, it gets dropped.
export function hasUsablePosition(n: { lat: number | null; lon: number | null }): n is { lat: number; lon: number } {
  return (
    n.lat != null &&
    n.lon != null &&
    !(Math.abs(n.lat) <= MESHVIEW_NULL_ISLAND_EPSILON_DEG && Math.abs(n.lon) <= MESHVIEW_NULL_ISLAND_EPSILON_DEG)
  );
}

const EARTH_RADIUS_KM = 6371;

// Great-circle ground distance between two positions, in km. Used ONLY to
// sanity-check a single edge's own endpoints against
// MESHVIEW_MAX_PLAUSIBLE_HOP_KM (definitions.ts) below — see that constant's
// own comment for why a "proven"/"evidenced" edge (including every real
// traceroute hop) still needs this check, not just "inferred"/"assumed"
// ones. No existing haversine helper elsewhere in this frontend to reuse
// (checked before adding a second one).
function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Builds one packet's animated route-tree walk from the backend's
// route-tree response. Returns null when there's nothing drawable at all
// (no root position, or every edge missing a usable parent/child position)
// — same "no known position, no draw" rule as everywhere else in this app,
// never a guessed placement. A (0, 0) root (or edge endpoint) now genuinely
// falls through this early return, same as any other unusable position, rather
// than half-rendering from a sentinel origin.
//
// Traversal: edges are grouped by their own explicit `hop_level` (never
// re-derived — the backend's own contract is authoritative), processed
// ascending so a parent's own arrival is always already known by the time
// its children are placed. Within one hop level, edges are further grouped
// by their actual parent node (siblings), ranked by `rx_snr` descending (a
// real, honest secondary ordering — the route-tree contract carries no
// per-edge timestamp) and spread proportionally across
// MESHVIEW_ROUTE_SIBLING_SPREAD_MS so "the 11 children of one node launch
// in sequence, not as a clump." An edge whose parent was never reached
// (upstream position invalid, or itself skipped for the same reason) is
// dropped, and since its own arrival is consequently never recorded, its
// descendants are transitively dropped too — no extra bookkeeping needed.
//
// A `gap` edge (or, pre-existing but now
// far more common, a null `parent`) carries NO justified link into its
// own child — the backend explicitly refuses to bridge across a hop it
// could not place; there should never be a packet shown
// hopping clear across the entire map. This must never become a drawn
// line, and this function must never paper over the gap by connecting
// the child to whatever edge happens to precede it in the array — but
// the child is still real, recorded data (see build_traceroute_edges'
// own docstring), so it still gets its own independent flash when it has
// a usable position, and its own arrival is still recorded (approximated
// as hop_level * FLIGHT_MS — for a traceroute's own strictly linear
// chain this is exactly the timing an unbroken chain would have
// produced) so a REAL edge further downstream of it can still resume the
// normal chain instead of the whole rest of the route silently vanishing
// (verification requirement: hops on either side of a gap still render).
//
// One MeshviewRoutePath per usable, plausible-distance edge, PLUS one
// MeshviewReceiverFlash per every edge with a usable child position — the
// two are additive, not exclusive (Task 35 — see this section's header
// comment): `flashes.length` is close to the full edge count now;
// `paths.length` is the plausible-distance subset of the same edges, drawn
// on top of their own flash, not instead of it.
export function buildRouteWalk(tree: MeshviewRouteTreeResponse, now: number, priority: MeshviewQueuePriority): MeshviewRouteWalk | null {
  // If there's no origin to put it at, it gets dropped — no
  // animation at all. Checked FIRST, before any path/flash is ever built, so a
  // packet with no usable transmitter position never gets a partial walk
  // (see AppData.tsx's own releaseMeshviewPacket for the matching pulse-side
  // guard, using this same hasUsablePosition rule).
  if (!tree.root || !hasUsablePosition(tree.root)) return null;

  // A node's own arrival time — when the ONE traveller that reaches it
  // gets there, which is also the moment its own children's travellers
  // depart (the branching-wave cascade). Root "arrives" at t=0.
  const arrivalMs = new Map<string, number>();
  arrivalMs.set(tree.root.node_id, 0);

  const paths: MeshviewRoutePath[] = [];
  const flashes: MeshviewReceiverFlash[] = [];
  let maxFinishMs = 0;

  const byLevel = new Map<number, MeshviewRouteTreeEdge[]>();
  for (const e of tree.edges) {
    if (e.gap || !e.parent) {
      // Task 33: never draw a line here, and never infer this child is
      // adjacent to whatever edge precedes it in the array — but the
      // hop itself is real, so it still lights up independently, and its
      // arrival is still recorded so a later, genuinely-linked edge off
      // of it can keep the rest of the route rendering.
      if (hasUsablePosition(e.child)) {
        const arrival = e.hop_level * FLIGHT_MS;
        arrivalMs.set(e.child.node_id, arrival);
        maxFinishMs = Math.max(maxFinishMs, arrival);
        flashes.push({ lon: e.child.lon, lat: e.child.lat, confidence: e.confidence, startOffsetMs: arrival });
      }
      continue;
    }
    if (!hasUsablePosition(e.parent) || !hasUsablePosition(e.child)) continue;
    const arr = byLevel.get(e.hop_level) ?? [];
    arr.push(e);
    byLevel.set(e.hop_level, arr);
  }
  const levels = Array.from(byLevel.keys()).sort((a, b) => a - b);

  for (const level of levels) {
    const byParent = new Map<string, MeshviewRouteTreeEdge[]>();
    for (const e of byLevel.get(level)!) {
      // Safe: only pushed into byLevel above after confirming e.parent is
      // non-null and usable (never a gap edge) — TS just can't carry that
      // narrowing through the intervening array storage.
      const arr = byParent.get(e.parent!.node_id) ?? [];
      arr.push(e);
      byParent.set(e.parent!.node_id, arr);
    }
    for (const [parentId, siblings] of byParent) {
      const parentArrival = arrivalMs.get(parentId);
      if (parentArrival == null) continue; // parent itself never reached — this whole branch is unreachable
      const sorted = [...siblings].sort((a, b) => (b.rx_snr ?? -Infinity) - (a.rx_snr ?? -Infinity));
      const n = sorted.length;
      sorted.forEach((e, idx) => {
        const withinGroupSpread = n > 1 ? (idx / n) * MESHVIEW_ROUTE_SIBLING_SPREAD_MS : 0;
        const startOffsetMs = parentArrival + withinGroupSpread;
        const totalTravelMs = FLIGHT_MS; // one hop, constant speed
        // This edge's own arrival — one hop's worth of travel time past its
        // own departure — is what its CHILDREN's travellers depart from,
        // regardless of whether THIS edge ends up a drawn path or a flash.
        arrivalMs.set(e.child.node_id, startOffsetMs + totalTravelMs);
        maxFinishMs = Math.max(maxFinishMs, startOffsetMs + totalTravelMs);

        // Task 34 (2026-09-21): confidence tier no longer gates whether a
        // line is drawn (see this section's header comment for why the
        // prior JUSTIFIED-only gate was reverted) — every edge with a
        // usable parent and child is a candidate line. Task 32's distance
        // backstop still applies to ALL of them, not just proven/evidenced:
        // a bad stored position on either endpoint can make an edge span a
        // physically impossible distance (measured up to 11,818km on real
        // traceroute data; see MESHVIEW_MAX_PLAUSIBLE_HOP_KM's own comment
        // in definitions.ts for the full measurement and the ceiling this
        // reuses). An edge beyond that ceiling falls through to the same
        // flash-only treatment — the child was still genuinely reached
        // (that isn't in question), only the specific parent->child
        // geometry is unbelievable, so no line is asserted but the
        // receiver still honestly lights up. This never discards the REST
        // of the same route: each edge in the tree is still evaluated and
        // drawn (or flashed) independently.
        const plausible =
          haversineKm(
            { lat: e.parent!.lat as number, lon: e.parent!.lon as number },
            { lat: e.child.lat as number, lon: e.child.lon as number },
          ) <= MESHVIEW_MAX_PLAUSIBLE_HOP_KM;
        if (plausible) {
          // Task 35 (2026-09-21): a line ALONE isn't enough — the header
          // comment's own measurement is why: a real hop is median under
          // 10km, which at region-wide default zoom is ~6 screen pixels,
          // too small to read on its own. So this is no longer either/or —
          // every plausible edge gets BOTH a line (the path it took) and a
          // flash at its own child (the fact it was heard there), timed to
          // land together: the flash's startOffsetMs is this edge's own
          // arrival (startOffsetMs + totalTravelMs), the same instant the
          // line's travelling head finishes crossing it, so the line
          // arriving and the node lighting up read as one event, not two.
          paths.push({
            segments: [
              {
                parent: { lon: e.parent!.lon as number, lat: e.parent!.lat as number },
                child: { lon: e.child.lon as number, lat: e.child.lat as number },
                confidence: e.confidence,
                provenance: e.provenance,
              },
            ],
            startOffsetMs,
            totalTravelMs,
          });
        }
        // Every edge whose child has a usable position flashes — always,
        // whether or not it also got a line above. This is what a gap/
        // null-parent edge already did (see the top of this loop); an
        // over-distance edge falls through to this same unconditional
        // flash now that "plausible" no longer gates it.
        flashes.push({
          lon: e.child.lon as number,
          lat: e.child.lat as number,
          confidence: e.confidence,
          startOffsetMs: startOffsetMs + totalTravelMs,
        });
      });
    }
  }

  if (paths.length === 0 && flashes.length === 0) return null;
  return {
    key: meshviewAnimationId(tree.meshview_packet_id),
    packetId: tree.meshview_packet_id,
    paths,
    flashes,
    createdAt: now,
    priority,
    maxFinishMs,
  };
}

// A walk's own true expiry: its single latest path's own worst-case finish
// + hold + fade, computed from the actual tree rather than a guessed fixed
// constant (tree depth and shape vary packet to packet).
export function pruneExpiredRouteWalks(list: MeshviewRouteWalk[], now: number): MeshviewRouteWalk[] {
  const trueLifetime = (w: MeshviewRouteWalk) => w.maxFinishMs + MESHVIEW_RECEPTION_HOLD_MS + MESHVIEW_RECEPTION_FADE_MS;
  return list.filter((w) => now - w.createdAt < trueLifetime(w));
}

// --- path geometry sampling (comet head + trail) ---------------------------
// Given a path and how far into its OWN travel we are (0..totalTravelMs,
// constant FLIGHT_MS per segment), finds the current position and which
// segment it falls in (for that segment's own confidence styling) — used
// both for the leading head (elapsedIntoTravel = "now") and for each trail
// slice (elapsedIntoTravel = "now minus some age", see
// lib/animation.ts's MESHVIEW_TRAIL_MS/SLICES).
export function positionAlongPath(
  segments: MeshviewRoutePathSegment[],
  elapsedIntoTravel: number,
): { lon: number; lat: number; confidence: MeshviewRouteConfidence | string; provenance: string } {
  const clamped = Math.max(0, elapsedIntoTravel);
  const idx = Math.min(segments.length - 1, Math.floor(clamped / FLIGHT_MS));
  const seg = segments[idx];
  const segT = Math.min(1, Math.max(0, (clamped - idx * FLIGHT_MS) / FLIGHT_MS));
  return {
    lon: seg.parent.lon + (seg.child.lon - seg.parent.lon) * segT,
    lat: seg.parent.lat + (seg.child.lat - seg.parent.lat) * segT,
    confidence: seg.confidence,
    provenance: seg.provenance,
  };
}

// The comet trail behind a travelling head: MESHVIEW_TRAIL_SLICES small
// consecutive segments sampled backward from the head over
// MESHVIEW_TRAIL_MS of travel time, each carrying its own opacity — bright
// just behind the head, fading slowly to 0 at the tail. Each path
// is now exactly one edge (see buildRouteWalk's header comment — the
// branching-wave fix), so a trail never actually straddles a confidence or
// provenance boundary; `positionAlongPath`/this function stay generic over
// `segments.length` regardless, rather than hardcoding "exactly one."
export function trailSlices(
  segments: MeshviewRoutePathSegment[],
  headElapsedIntoTravel: number,
): { from: { lon: number; lat: number }; to: { lon: number; lat: number }; confidence: MeshviewRouteConfidence | string; provenance: string; opacityFraction: number }[] {
  const slices: ReturnType<typeof trailSlices> = [];
  const points: { lon: number; lat: number; confidence: MeshviewRouteConfidence | string; provenance: string }[] = [];
  for (let i = 0; i <= MESHVIEW_TRAIL_SLICES; i++) {
    const t = headElapsedIntoTravel - (i / MESHVIEW_TRAIL_SLICES) * MESHVIEW_TRAIL_MS;
    if (t < 0) break;
    points.push(positionAlongPath(segments, t));
  }
  for (let i = 0; i < points.length - 1; i++) {
    slices.push({
      from: points[i],
      to: points[i + 1],
      confidence: points[i].confidence,
      provenance: points[i].provenance,
      // 1 at the head end (i=0), fading linearly to 0 at the tail end.
      opacityFraction: 1 - i / MESHVIEW_TRAIL_SLICES,
    });
  }
  return slices;
}
